import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'react-hot-toast';
import { Loader2, AlertCircle, RefreshCw, DollarSign, TrendingUp, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ARAgingReport = () => {
  const navigate = useNavigate();
  const [agingData, setAgingData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const drillTo = (customerId, bucket = null) => {
    const qs = bucket ? `?bucket=${encodeURIComponent(bucket)}` : '';
    navigate(`/ar/customers/${encodeURIComponent(customerId)}/open-items${qs}`);
  };

  useEffect(() => {
    loadAgingReport();
  }, []);

  const loadAgingReport = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ar/ar-aging`);
      setAgingData(response.data || []);
    } catch (error) {
      console.error('Error loading AR aging report:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load AR aging report: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate totals
  const totals = agingData.reduce((acc, item) => ({
    total_open: acc.total_open + (item.total_open || 0),
    current: acc.current + (item.current || 0),
    days_1_30: acc.days_1_30 + (item.days_1_30 || 0),
    days_31_60: acc.days_31_60 + (item.days_31_60 || 0),
    days_61_90: acc.days_61_90 + (item.days_61_90 || 0),
    days_over_90: acc.days_over_90 + (item.days_over_90 || 0)
  }), {
    total_open: 0,
    current: 0,
    days_1_30: 0,
    days_31_60: 0,
    days_61_90: 0,
    days_over_90: 0
  });

  const totalOverdue = totals.days_1_30 + totals.days_31_60 + totals.days_61_90 + totals.days_over_90;

  const formatCurrency = (amount) => {
    return `USD ${(amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AR Aging Report</h1>
            <p className="text-sm text-gray-600 mt-1">Accounts receivable aging analysis by customer</p>
          </div>
          <Button variant="outline" onClick={loadAgingReport} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-gradient-to-r from-orange-50 to-orange-50">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-xs text-gray-600">Total Open</p>
                <p className="text-xl font-bold text-orange-800">{formatCurrency(totals.total_open)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-xs text-gray-600">Current (Not Due)</p>
                <p className="text-xl font-bold text-green-800">{formatCurrency(totals.current)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-xs text-gray-600">Total Overdue</p>
                <p className="text-xl font-bold text-orange-800">{formatCurrency(totalOverdue)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-r from-red-50 to-pink-50">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-xs text-gray-600">Over 90 Days</p>
                <p className="text-xl font-bold text-red-800">{formatCurrency(totals.days_over_90)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Aging Table */}
        <Card className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              <span className="ml-3 text-gray-600">Loading aging report...</span>
            </div>
          ) : agingData.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No open receivables found.</p>
              <p className="text-sm text-gray-400 mt-1">Create sales orders and release billing documents to see data here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="aging-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Customer</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Open</th>
                    <th className="text-right py-3 px-4 font-semibold text-green-700 bg-green-50">Current</th>
                    <th className="text-right py-3 px-4 font-semibold text-yellow-700 bg-yellow-50">1-30 Days</th>
                    <th className="text-right py-3 px-4 font-semibold text-orange-700 bg-orange-50">31-60 Days</th>
                    <th className="text-right py-3 px-4 font-semibold text-red-600 bg-red-50">61-90 Days</th>
                    <th className="text-right py-3 px-4 font-semibold text-red-800 bg-red-100">Over 90 Days</th>
                  </tr>
                </thead>
                <tbody>
                  {agingData.map((item) => (
                    <tr
                      key={item.customer_id}
                      className="border-b hover:bg-gray-50"
                      data-testid={`ar-row-${item.customer_id}`}
                    >
                      <td
                        className="py-3 px-4 cursor-pointer"
                        onClick={() => drillTo(item.customer_id)}
                        title="View all open items"
                      >
                        <div>
                          <p className="font-medium text-orange-700 hover:underline">{item.customer_name || item.customer_id}</p>
                          <p className="text-xs text-gray-500">{item.customer_id}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold cursor-pointer hover:bg-orange-50" onClick={() => drillTo(item.customer_id)}>
                        {formatCurrency(item.total_open)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono bg-green-50 cursor-pointer hover:bg-green-100" onClick={() => drillTo(item.customer_id, 'current')}>
                        {item.current > 0 ? formatCurrency(item.current) : '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono bg-yellow-50 cursor-pointer hover:bg-yellow-100" onClick={() => drillTo(item.customer_id, 'days_1_30')}>
                        {item.days_1_30 > 0 ? (
                          <span className="text-yellow-700">{formatCurrency(item.days_1_30)}</span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono bg-orange-50 cursor-pointer hover:bg-orange-100" onClick={() => drillTo(item.customer_id, 'days_31_60')}>
                        {item.days_31_60 > 0 ? (
                          <span className="text-orange-700 font-medium">{formatCurrency(item.days_31_60)}</span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono bg-red-50 cursor-pointer hover:bg-red-100" onClick={() => drillTo(item.customer_id, 'days_61_90')}>
                        {item.days_61_90 > 0 ? (
                          <span className="text-red-600 font-medium">{formatCurrency(item.days_61_90)}</span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono bg-red-100 cursor-pointer hover:bg-red-200" onClick={() => drillTo(item.customer_id, 'days_over_90')}>
                        {item.days_over_90 > 0 ? (
                          <span className="text-red-800 font-bold">{formatCurrency(item.days_over_90)}</span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className="border-t-2 border-gray-300 bg-gray-100 font-bold">
                    <td className="py-3 px-4">TOTAL</td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(totals.total_open)}</td>
                    <td className="py-3 px-4 text-right font-mono bg-green-100">{formatCurrency(totals.current)}</td>
                    <td className="py-3 px-4 text-right font-mono bg-yellow-100 text-yellow-700">{formatCurrency(totals.days_1_30)}</td>
                    <td className="py-3 px-4 text-right font-mono bg-orange-100 text-orange-700">{formatCurrency(totals.days_31_60)}</td>
                    <td className="py-3 px-4 text-right font-mono bg-red-100 text-red-600">{formatCurrency(totals.days_61_90)}</td>
                    <td className="py-3 px-4 text-right font-mono bg-red-200 text-red-800">{formatCurrency(totals.days_over_90)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Legend */}
        <Card className="p-4 mt-6 bg-gray-50">
          <h4 className="text-sm font-semibold mb-2">Aging Buckets</h4>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-100 rounded"></div>
              <span>Current: Not yet due</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-100 rounded"></div>
              <span>1-30 Days: Slightly overdue</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-100 rounded"></div>
              <span>31-60 Days: Moderately overdue</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-100 rounded"></div>
              <span>61-90 Days: Significantly overdue</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-200 rounded"></div>
              <span>Over 90 Days: Critical</span>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default ARAgingReport;
